import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@clerk/react';
import { useApiClient } from './use-api-client.js';
import type { QualityLevel } from '@agentbrush/shared';

export interface GenerationRecord {
  id: string;
  timestamp: string;
  preset: string;
  qualityHint: QualityLevel;
  tokenCost: number;
  promptHash: string;
}

interface RecentGenerationsResponse {
  ok: boolean;
  data: { generations: GenerationRecord[] };
}

export const generationsQueryKey = (userId: string | null | undefined, limit: number) =>
  ['generations', userId ?? 'anon', limit] as const;

export function useGenerationsQuery({ limit = 10 }: { limit?: number } = {}) {
  const { fetchApi } = useApiClient();
  const { userId } = useAuth();

  return useQuery<GenerationRecord[]>({
    queryKey: generationsQueryKey(userId, limit),
    queryFn: async () => {
      const res = await fetchApi(`/v1/generations/recent?limit=${String(limit)}`);
      if (!res.ok) throw new Error(`Status ${res.status}`);
      const body = (await res.json()) as RecentGenerationsResponse;
      return body.data?.generations ?? [];
    },
    enabled: Boolean(userId),
    // Keep the previous batch on screen while the next, larger one fetches —
    // otherwise "Load more" briefly flashes the skeleton over already-rendered
    // rows. New in @tanstack/react-query v5 (replaces keepPreviousData).
    placeholderData: (prev) => prev,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
}
